import { mapPersonType, PersonType } from "@/lib/utils";
import { CustomerKYCResponse } from "../entities/CustomerKYC";
import {
  createDefaultReference,
  createDefaultCustomerProfilData,
} from "../entities/defaults";
import type { CodeName } from "../entities/ProspectsTypes";
import {
  Gender,
  Reference,
  Address,
  IdPaper,
  RiskLevel,
} from "../entities/shared";
import {
  ApiProspectByBusinessKeyResponse,
  ApiIdPaper,
} from "./ProspectsPipelineMapper";

/**
 * Maps getProspect API response to CustomerKYCData
 * Uses ApiProspectByBusinessKeyResponse structure from ProspectsPipelineMapper
 */
export class CustomerKYCMapper {
  private static mapYesNoBoolean(
    value: boolean | string | null | undefined,
  ): boolean | null {
    if (value == null) return null;
    if (typeof value === "boolean") return value;
    if (value === "O") return true;
    if (value === "N") return false;
    return null;
  }

  /**
   * Maps getProspect API response to CustomerKYCResponse
   */
  static fromProspectApiResponse(apiResponse: {
    code: string;
    message: string;
    data: ApiProspectByBusinessKeyResponse;
  }): CustomerKYCResponse {
    const apiData = apiResponse.data;
    const spouseLastName =
      (apiData as { spouse_last_name?: string | null }).spouse_last_name ??
      (apiData as { spouseLastName?: string | null }).spouseLastName ??
      (apiData as { partnerLastName?: string | null }).partnerLastName ??
      null;
    const spouseFirstName =
      (apiData as { spouse_first_names?: string | null }).spouse_first_names ??
      (apiData as { spouseFirstName?: string | null }).spouseFirstName ??
      (apiData as { partnerFirstName?: string | null }).partnerFirstName ??
      null;
    const deathDate =
      (apiData as { deathDate?: string | null }).deathDate ??
      apiData.death_date ??
      null;
    const taxIdNumber =
      (
        apiData as {
          tax_id_number?: string | null;
          taxIdNumber?: string | null;
        }
      ).tax_id_number ??
      (
        apiData as {
          tax_id_number?: string | null;
          taxIdNumber?: string | null;
        }
      ).taxIdNumber ??
      null;
    const defaultData = createDefaultCustomerProfilData();

    return {
      code: apiResponse.code,
      message: apiResponse.message,
      data: {
        ...defaultData,
        id: apiData.business_key,
        businessKey: apiData.business_key,
        customerCode: apiData.customerCode || "",
        firstName: apiData.first_name || "",
        lastname: apiData.last_name || "",
        segment: mapPersonType(apiData?.type as PersonType, "full"),
        gender: this.mapGender(apiData.gender),
        email1: apiData.email1 || "",
        email1Verified: apiData.email1Verified ?? false,
        email2: apiData.email2 || null,
        email2Verified: apiData.email2Verified ?? false,
        phone1: apiData.phone1 || "",
        phone1CountryCode: apiData.phone1CountryCode || null,
        phone1Verified: apiData.phone1Verified ?? false,
        phone2: apiData.phone2 || null,
        phone2CountryCode: apiData.phone2CountryCode || null,
        phone2Verified: apiData.phone2Verified ?? false,
        phone3: apiData.phone3 ?? null,
        phone3CountryCode: apiData.phone3CountryCode ?? null,
        phone3Verified: apiData.phone3Verified ?? false,
        phone4: apiData.phone4 ?? null,
        phone4CountryCode: apiData.phone4CountryCode ?? null,
        phone4Verified: apiData.phone4Verified ?? false,
        fax: apiData.fax ?? null,
        pep: apiData.peps ?? false,
        taxIdNumber,
        nationality: this.mapCodeName(apiData.nationality),
        addresses: this.mapAddress(apiData),
        birthDate: apiData.birthDate || null,
        birthPlace: apiData.birthPlace || null,
        idPaper: this.mapIdPaper(apiData.idPaper),

        civility: apiData.civility ? this.mapCodeName(apiData.civility) : null,
        familyStatus: this.mapCodeName(apiData.family_status),
        countryResidence: this.toCodeName(apiData.country_residence),
        resident: apiData.resident ?? false,
        numberChildren: apiData.number_children ?? null,
        comment: apiData.comment ?? null,
        contactForSavingCardCredit: apiData.contactForSavingCardCredit ?? null,
        products: Array.isArray(apiData.products)
          ? apiData.products
          : apiData.produits
            ? [apiData.produits]
            : [],
        profession: apiData.profession ?? null,
        employer: apiData.employer ?? null,
        monthlyIncome: apiData.monthly_income ?? null,
        sectorActivity: apiData.sector_activity ?? null,
        qualityCode: apiData.qualityCode ?? apiData.quality_code ?? null,

        legalCapacity: this.mapCodeName(apiData.legal_capacity),
        legalSituation: this.mapCodeName(apiData.legal_situation),
        legalRepresentative:
          apiData.legal_representative ?? apiData.legal_referent ?? null,
        fatherLastName: apiData.fatherLastName ?? null,
        motherMaidenName: apiData.motherMaidenName ?? null,
        fatherFirstNames: apiData.fatherFirstName ?? null,
        motherFirstNames: apiData.motherFirstName ?? null,
        deathDate,
        matrimonialRegime: apiData.matrimonial_regime
          ? this.mapCodeName(apiData.matrimonial_regime)
          : null,
        marriageContract: apiData.marriage_contract ?? null,
        spouseLastName,
        spouseFirstName,
        relationOtherFinancialInstitutions: this.mapYesNoBoolean(
          apiData.otherBankRelation,
        ),
        bankEmployee: this.mapYesNoBoolean(apiData.bankPersonnel),
        professionCountry: this.toProfessionCountry(
          apiData.profession_country ?? apiData.paysProfession ?? null,
        ),

        patrimoineRealEstateCount:
          apiData.patrimoine_real_estate_count ??
          apiData.patrimoineRealEstateCount ??
          null,
        patrimoineRealEstateAssets:
          apiData.patrimoine_real_estate_assets ??
          apiData.patrimoineRealEstateAssets ??
          null,
        patrimoineRealEstateInsured:
          apiData.patrimoine_real_estate_insured ??
          apiData.patrimoineRealEstateInsured ??
          null,
        patrimoineRealEstateEstimatedValue:
          apiData.patrimoine_real_estate_estimated_value ??
          apiData.patrimoineRealEstateEstimatedValue ??
          null,
        patrimoineOtherBankAccountsValue:
          apiData.patrimoine_other_bank_accounts_value ??
          apiData.patrimoineOtherBankAccountsValue ??
          null,
        patrimoineOtherBankAccounts:
          apiData.patrimoine_other_bank_accounts ??
          apiData.patrimoineOtherBankAccounts ??
          null,
        patrimoineValuablesInsured:
          apiData.patrimoine_valuables_insured ??
          apiData.patrimoineValuablesInsured ??
          null,
        patrimoineValuablesValue:
          apiData.patrimoine_valuables_value ??
          apiData.patrimoineValuablesValue ??
          null,
        patrimoineValuableObjects:
          apiData.patrimoine_valuable_objects ??
          apiData.patrimoineValuableObjects ??
          null,
        situationPatrimoniale:
          apiData.situationPatrimoniale ??
          apiData.situation_patrimoniale ??
          null,
        sourceDesFonds:
          apiData.sourceDesFonds ?? apiData.source_des_fonds ?? null,
        objetRelationBancaire:
          apiData.objetRelationBancaire ??
          apiData.objet_relation_bancaire ??
          null,
        residenceFiscalePays:
          apiData.residenceFiscalePays ??
          apiData.residence_fiscale_pays ??
          null,
        ppe: apiData.ppe ?? null,
        fonctionPpeExercee:
          apiData.fonctionPpeExercee ?? apiData.fonction_ppe_exercee ?? null,
        beneficiaireEffectif:
          apiData.beneficiaireEffectif ?? apiData.beneficiaire_effectif ?? null,
        identiteBeneficiaireEffectif:
          apiData.identiteBeneficiaireEffectif ??
          apiData.identite_beneficiaire_effectif ??
          null,

        riskLevel: this.mapRiskLevel(apiData.risk_status),
        portrait_url: apiData.portrait_url ?? null,

        agencyCode: apiData.agencyCode ?? null,
        agencyName: apiData.agencyName ?? null,
        advisorLogin: apiData.advisorLogin ?? null,
        advisorName: apiData.advisorName ?? null,
      },
    };
  }

  /**
   * Maps the raw backend risk status to the domain RiskLevel type.
   */
  private static mapRiskLevel(
    riskStatus: string | null | undefined,
  ): RiskLevel {
    if (!riskStatus) return null;
    const riskMap: Record<string, RiskLevel> = {
      STANDARD: "STANDARD",
      MODERE: "STANDARD",
      DEFAUT: "STANDARD",
      HIGH: "HIGH",
      ELEVE: "HIGH",
      TRES_ELEVE: "HIGH",
      LOW: "LOW",
      FAIBLE: "LOW",
    };
    const input = riskStatus.toUpperCase();
    return Object.hasOwn(riskMap, input) ? riskMap[input] : null;
  }

  /**
   * Pass through API code/name as CodeName (no conversion to Reference)
   */
  private static toCodeName(
    value: { code: string; name: string } | null | undefined,
  ): CodeName | null {
    if (!value || !value.code) return null;
    return { code: value.code, name: value.name };
  }

  private static toProfessionCountry(
    value: { code: string; name: string } | string | null | undefined,
  ): CodeName | null {
    if (!value) return null;
    if (typeof value === "string") {
      const code = value.trim();
      if (!code) return null;
      return { code, name: code };
    }

    if (!value.code) return null;
    return { code: value.code, name: value.name || value.code };
  }

  /**
   * Maps gender from API to domain Gender type
   */
  private static mapGender(
    apiGender: "MALE" | "FEMALE" | null | undefined,
  ): Gender {
    if (!apiGender) return "OTHER";
    return apiGender === "MALE" ? "MALE" : "FEMALE";
  }

  /**
   * Maps code/name structure to Reference
   */
  private static mapCodeName(
    codeName: { code: string; name: string } | null | undefined,
  ): Reference {
    if (!codeName || !codeName.code) {
      return createDefaultReference();
    }

    return {
      id: 0, // Not available in API
      code: codeName.code,
      label: codeName.name,
    };
  }

  /**
   * Maps idPaper from API to domain IdPaper type
   */
  private static mapIdPaper(api?: ApiIdPaper | null): IdPaper | null {
    if (!api) return null;
    const issuingRaw = api.issuing_country ?? api.issuingCountry;
    const issuingCountry =
      issuingRaw && (issuingRaw.code || issuingRaw.name)
        ? {
            code: issuingRaw.code ?? "",
            name: issuingRaw.name ?? "",
          }
        : null;
    return {
      id: api.id,
      type: api.type || "",
      idPaperNumber: api.idPaperNumber || "",
      nni: api.nni ?? null,
      deliveryDate: api.deliveryDate || "",
      validityDate: api.validityDate || "",
      deliveryPlace: api.deliveryPlace || "",
      organisationWhichDeliver: api.organisationWhichDeliver ?? null,
      issuingCountry,
      documentReference:
        api.document_reference ?? api.documentReference ?? null,
    };
  }

  /**
   * Maps flat address fields from API to nested Address structure
   */
  private static mapAddress(
    apiData: ApiProspectByBusinessKeyResponse,
  ): Address {
    return {
      id: "", // Not available in API
      type: "HOME", // Default value
      street: apiData.address || null,
      postalAddress: apiData.postal_address ?? apiData.postalAddress ?? null,
      since: apiData.since ?? apiData.since_at ?? null,
      area: null, // Not available in API
      city: apiData.city || null,
      localGovernment: null, // Not available in API
      postalCode: apiData.postal_code || null,
      country: this.mapCodeName(apiData.country_residence),
      geoLocation: null, // Not available in API
    };
  }
}
